import { Grid, TextField } from "@mui/material";
import { useDebounce } from "hooks/utils/debounce";
import { useEffectEvent } from "hooks/utils/effect-event";
import { type Dispatch, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ColorCieLab,
  type ColorCieLch,
  labToLch,
  lchToLab,
  roundColor,
} from "service/color-math";
import { useAppConfig } from "../../../config/app-config";
import type { NamedColor } from "./ColorFilter";

export interface ColorFilterLchProps {
  onChange: Dispatch<NamedColor | undefined>;
  value?: ColorCieLab;
  validationRanges?: {
    l: { min: number; max: number };
    c: { min: number; max: number };
    h: { min: number; max: number };
  };
}

type ColorLchForm = [string, string, string];
const formToColor = (value: ColorLchForm) =>
  value.map(parseValue) as ColorCieLch;

const colorToForm = (input: ColorCieLab | undefined) => {
  const value = input
    ? roundColor(labToLch(input))
    : [undefined, undefined, undefined];
  return value.map((v) => v?.toString() ?? "") as ColorLchForm;
};

const defaultValidationRanges = {
  l: { min: 0, max: 100 },
  c: { min: 0, max: 100 },
  h: { min: 0, max: 360 },
};

function parseValue(val: string) {
  return +val.replace(",", ".");
}

export const ColorFilterLch = ({
  value,
  onChange,
  validationRanges = defaultValidationRanges,
}: ColorFilterLchProps) => {
  const { t } = useTranslation();
  const [formValue, setFormValue] = useState<ColorLchForm>(colorToForm(value));
  const [touched, setTouched] = useState([false, false, false]);
  const [appConfig] = useAppConfig();
  const debouncedFormValue = useDebounce(
    formValue,
    appConfig.ux.general.inputDebounceTimeMs,
  );

  const validate = (value: ColorLchForm) => {
    const ranges = [validationRanges.l, validationRanges.c, validationRanges.h];
    return value.map((val, idx) => {
      if (val === "") {
        return t("validation_required");
      }
      const numberValue = parseValue(val);
      if (Number.isNaN(numberValue)) {
        return t("validation_invalid_format");
      }
      const range = ranges[idx];
      return numberValue < range.min || numberValue > range.max
        ? t("validation_range", { min: range.min, max: range.max })
        : undefined;
    });
  };

  const validationErrors = validate(formValue);

  const onFormSubmit = useEffectEvent((val: ColorLchForm) => {
    if (Object.values(validate(val)).every((v) => !v)) {
      const value = lchToLab(formToColor(val));
      onChange({
        name: t("color_system_cie_lch"),
        value: value,
      });
    }
  });

  useEffect(() => {
    if (debouncedFormValue) {
      onFormSubmit(debouncedFormValue);
    }
  }, [debouncedFormValue, onFormSubmit]);

  const onTouched = (i: number) => {
    setTouched((old) => {
      const newVal = [...old];
      newVal[i] = true;
      return newVal;
    });
  };
  const onFormChange = (i: number, value: string) => {
    if (formValue[i] === value) return;
    onTouched(i);
    setFormValue((old) => {
      const newVal = [...old];
      newVal[i] = value;
      return newVal as ColorLchForm;
    });
  };

  return (
    <Grid size={{ xs: 12, md: 6, xl: 3 }}>
      <Grid container spacing={2}>
        <Grid size={4}>
          <TextField
            label="L"
            value={formValue[0]}
            error={touched[0] && Boolean(validationErrors[0])}
            fullWidth
            required
            onChange={(e) => onFormChange(0, e.target.value)}
            onBlur={() => onTouched(0)}
            helperText={touched[0] && validationErrors[0]}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="C"
            value={formValue[1]}
            error={touched[1] && Boolean(validationErrors[1])}
            fullWidth
            required
            onChange={(e) => onFormChange(1, e.target.value)}
            onBlur={() => onTouched(1)}
            helperText={touched[1] && validationErrors[1]}
          />
        </Grid>
        <Grid size={4}>
          <TextField
            label="h"
            value={formValue[2]}
            error={touched[2] && Boolean(validationErrors[2])}
            fullWidth
            required
            onChange={(e) => onFormChange(2, e.target.value)}
            onBlur={() => onTouched(2)}
            helperText={touched[2] && validationErrors[2]}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
